import type { FC } from "react"
import {
	IconCheckOutline18 as Check,
	IconLink2SlashOutline18 as Link2Off,
} from "nucleo-ui-outline-18"
import { FixedSizeList } from "react-window"

import { cn } from "@productlane/lib/src/cn"
import { CommandItem } from "@productlane/ui/src/components/internal/command"

import type { SharedFilterItemsProps, WithQuery } from "@/components/filters"
import { companyDomain } from "@/lib/companies"
import { useCompanyOptions } from "@/lib/use-entity-options"
import { CompanyLogo } from "@/screens/app/customers/company-logo"
import { useRoadmapFilterState } from "../roadmap-filter-state"

export const CompanyFilterItems: FC<SharedFilterItemsProps & WithQuery> = ({
	onSelect,
	query,
}) => {
	const { companyIds, addCompany, removeCompany } = useRoadmapFilterState()
	const { companies: filteredCompanies } = useCompanyOptions({
		query,
		selectedIds: companyIds,
	})

	return (
		<>
			<CommandItem
				key="no-company"
				onSelect={() => {
					if (companyIds.includes(null)) {
						removeCompany(null)
					} else {
						addCompany(null)
					}
					onSelect()
				}}
			>
				<div className="flex w-full items-center gap-2">
					<Link2Off className="h-4 w-4 shrink-0" />
					<span className="flex-1 truncate">No company</span>
					<Check
						className={cn("h-4 w-4 shrink-0", {
							"opacity-100": companyIds.includes(null),
							"opacity-0": !companyIds.includes(null),
						})}
					/>
				</div>
			</CommandItem>
			<hr className="my-2" />

			{filteredCompanies.length ? (
				<div className="h-64">
					<FixedSizeList
						height={256}
						width="100%"
						itemCount={filteredCompanies.length}
						itemData={filteredCompanies}
						itemKey={(index, data) => data[index]?.id ?? index}
						itemSize={36}
						overscanCount={5}
						className="!h-full"
					>
						{({ data, index, style }) => {
							const company = data[index]
							if (!company) return null
							const isSelected = companyIds.includes(company.id)
							return (
								<CommandItem
									value={company.id}
									key={company.id}
									style={style}
									onSelect={() => {
										if (isSelected) {
											removeCompany(company.id)
										} else {
											addCompany(company.id)
										}
										onSelect()
									}}
								>
									<div className="flex w-full items-center gap-2">
										<CompanyLogo
											linearLogoUrl={company.logoUrl ?? undefined}
											brandfetchData={null}
											fallback={company.name}
											size="xs"
										/>
										<span className="flex min-w-0 flex-1 items-center gap-1.5">
											<span className="truncate text-primary">
												{company.name}
											</span>
											{companyDomain(company) ? (
												<span className="truncate text-secondary">
													{companyDomain(company)}
												</span>
											) : null}
										</span>
										<Check
											className={cn("h-4 w-4 shrink-0", {
												"opacity-100": isSelected,
												"opacity-0": !isSelected,
											})}
										/>
									</div>
								</CommandItem>
							)
						}}
					</FixedSizeList>
				</div>
			) : (
				<div className="py-4 text-center text-sm text-secondary">
					No companies found
				</div>
			)}
		</>
	)
}
